import { useCallback, useEffect, useState } from 'react';
import { showErrorToast } from '../notifications/toast';
import type { DiscussionMessage, FeedbackTargetKind, SiteFeedbackApi } from './types';

export function useFeedbackDiscussion(
  api: SiteFeedbackApi,
  kind: FeedbackTargetKind | null,
  ticketId: string | null,
  enabled: boolean
) {
  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(async () => {
    if (!kind || !ticketId || !enabled) {
      setMessages([]);
      return;
    }
    setLoading(true);
    try {
      setMessages(await api.listDiscussion(kind, ticketId));
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load discussion.');
    } finally {
      setLoading(false);
    }
  }, [api, enabled, kind, ticketId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const post = useCallback(
    async (body: string) => {
      if (!kind || !ticketId) return;
      await api.postDiscussion(kind, ticketId, body);
      await reload();
    },
    [api, kind, reload, ticketId]
  );

  const remove = useCallback(
    async (messageId: string) => {
      await api.deleteDiscussionMessage(messageId);
      await reload();
    },
    [api, reload]
  );

  return { messages, loading, reload, post, remove };
}
